import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { ImageBannerElementDataType } from '@/components/sections/builder/elements/body/image-banner/types';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ImageBannerElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  const HEIGHT_SIZE_OPTIONS = [
    {
      label: 'Adapt to first image',
      value: 'adapt',
    },
    {
      label: 'Small',
      value: 'small',
    },
    {
      label: 'Medium',
      value: 'medium',
    },
    {
      label: 'Large',
      value: 'large',
    },
  ];

  const CONTENT_POSITION_OPTIONS = [
    { label: 'Top Left', value: 'top-left' },
    { label: 'Top Center', value: 'top-center' },
    { label: 'Top Right', value: 'top-right' },
    { label: 'Middle Left', value: 'middle-left' },
    { label: 'Middle Center', value: 'middle-center' },
    { label: 'Middle Right', value: 'middle-right' },
    { label: 'Bottom Left', value: 'bottom-left' },
    { label: 'Bottom Center', value: 'bottom-center' },
    { label: 'Bottom Right', value: 'bottom-right' },
  ];

  return (
    <div>
      <div className='mb-4 font-bold'>First image</div>
      <ImageSetting
        value={data?.firstImage}
        onChange={(v) => builder.setElementData(element.id, { firstImage: v })}
      />
      <div className='mb-4 mt-4'>First mobile image (optional)</div>
      <ImageSetting
        value={data?.firstImageMobile}
        onChange={(v) => builder.setElementData(element.id, { firstImageMobile: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Second image</div>
      <ImageSetting
        value={data?.secondImage}
        onChange={(v) => builder.setElementData(element.id, { secondImage: v })}
      />
      <div className='mb-4 mt-4'>Second mobile image (optional)</div>
      <ImageSetting
        value={data?.secondImageMobile}
        onChange={(v) => builder.setElementData(element.id, { secondImageMobile: v })}
      />
      <InputSlider
        label='Device under this size use mobile image'
        value={data.mobileMinWidth || 0}
        max={1440}
        min={0}
        rightContent={<div>px</div>}
        onChange={(val) =>
          builder.setElementData(element.id, {
            mobileMinWidth: val,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Banner height</div>
      <div className='mb-4'>
        <Select
          name='bgScheme'
          value={data.height || 'fit'}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              height: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {HEIGHT_SIZE_OPTIONS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showContainerDesktop'
          aria-label='Select'
          className='mx-2'
          checked={data.showContainerDesktop}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, { showContainerDesktop: checked });
          }}
        />
        <label htmlFor='showContainerDesktop'>Show container on desktop</label>
      </div>
      <div className='mb-4 mt-4 font-bold'>Desktop content position</div>
      <div className='mb-4'>
        <Select
          name='bgScheme'
          value={data.contentPosition}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              contentPosition: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {CONTENT_POSITION_OPTIONS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mb-4 mt-4 font-bold'>Desktop content alignment</div>
      <InputTab
        defaultValue={data.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />
      <div className='mb-2 mt-4 font-bold'>Color scheme</div>
      <SchemeSetting
        value={data.containerColorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            containerColorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <div className='mb-4 mt-4 font-bold'>Animations</div>
      <div className='mb-4'>Image behavior</div>
      <div className='mb-4'>
        <Select
          name='backgroundAnimation'
          value={data.backgroundAnimation}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              backgroundAnimation: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='none'>None</SelectItem>
            <SelectItem value='ambient_movement'>Ambient movement</SelectItem>
            <SelectItem value='fixed'>Fixed background position</SelectItem>
            <SelectItem value='zoom_on_scroll'>Zoom in scroll</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className='mb-4 mt-4 font-bold'>Overlay</div>
      <ColorPicker
        color={data.overlay}
        inputColor={data.overlay}
        onChangeColorComplete={(color) => onChangeColor(color)}
        onChangeInputColor={(e) => builder.setElementData(element.id, { overlay: e.target.value })}
        onChangePercentage={(percent) => {
          builder.setElementData(element.id, {
            overlayOpacity: Number(percent),
          });
        }}
      />
      <div className='mb-2 mt-4 font-bold'>Mobile layout</div>
      <div className='mb-2 mt-4 text-xs'>Mobile content alignment</div>
      <InputTab
        defaultValue={data.contentAlignMobile}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlignMobile: v })}
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='stackImageMobile'
          aria-label='Select'
          className='mx-2'
          checked={data.stackImageMobile}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, { stackImageMobile: checked });
          }}
        />
        <label htmlFor='stackImageMobile'>Stack images on mobile</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showContainerMobile'
          aria-label='Select'
          className='mx-2'
          checked={data.showContainerMobile}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, { showContainerMobile: checked });
          }}
        />
        <label htmlFor='showContainerMobile'>Show container on mobile</label>
      </div>
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
    </div>
  );
}
